import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FormikState } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Header,
  Table,
  Loader,
  Checkbox,
  Dropdown,
  Form,
} from 'semantic-ui-react';
import { Icon } from 'semantic';
import { debounce, first, isEmpty, size, groupBy } from 'lodash-es';
import * as Yup from 'yup';

import InputField from 'components/form-fields/formik/InputField';
import { SelectableButton } from 'components/SelectableButton';
import { ActionButtonWithIcon } from 'components/semantic/ActionButtonWithIcon';
import Button from 'components/semantic/Button';
import { EvseController } from 'types/evse-controller';
import Pagination from 'components/Pagination';
import { request } from 'utils/api';
import SearchDropdown from 'components/form-fields/SearchDropdown';
import ErrorMessage from 'components/ErrorMessage';
import { ChargingGroupReviewModal } from './ChargingGroupReviewModal';
import {
  ConnectorsSelection,
  ConnectorSelectionValue,
  getConnectorSelectionKey,
  evseControllersToConnectorSelectionValues,
  ConnectorSelectionValueWithMetadata,
  EVSE_CONTROLLER_LIMIT,
  PowerType,
  powerTypeToFriendlyName,
  standardToFriendlyName,
  SearchFilter,
  FilterType,
} from './utils';
import { SetFieldValueFn } from 'subdomains/cpo/types/form';
import { useQuery } from '@tanstack/react-query';
import { Location } from 'types/location';
import { Account } from 'types/account';
import {
  ACCOUNTS_V1_FAST_SEARCH_API_URL,
  EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL,
  LOCATIONS_V2_CPO_FAST_SEARCH_API_URL,
} from 'subdomains/cpo/router/api';
import { ChargingGroupPreview } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-groups/ChargingGroupPreview';

export const validationSchema = Yup.object().shape({
  label: Yup.string().required('Name is required'),
  description: Yup.string().optional(),
  connectorsSelection: Yup.object()
    .shape({})
    .test(
      'at-least-one-connector',
      'At least one connector must be selected',
      (value) => {
        return size(value) > 0;
      }
    ),
});

/**
 * Form inputs for the charging group form
 */
export type ChargingGroupFormValues = {
  label: string;
  description: string;
  /**
   * Connectors selection determines which connectors are selected for on the form
   * The reason this is a map is because we will be possibily dealing with a lot of data
   * So, to avoid doing loops over and over again, we will store the connectors selection in a map
   */
  connectorsSelection: ConnectorsSelection;
};

const SEARCH_CATEGORY_FILTERS = [
  { key: 'location', text: 'Location', value: 'location' },
  { key: 'account', text: 'Account', value: 'account' },
  { key: 'powerType', text: 'Power type', value: 'powerType' },
];

const SEARCH_POWER_TYPE_FILTERS = [
  { key: PowerType.AC, text: PowerType.AC, value: PowerType.AC },
  { key: PowerType.DC, text: PowerType.DC, value: PowerType.DC },
  { key: PowerType.ACDC, text: PowerType.ACDC, value: PowerType.ACDC },
];

type ChargingGroupType = 'standard' | 'smart';

type Props = {
  values: FormikState<ChargingGroupFormValues>['values'];
  setFieldValue: SetFieldValueFn<ChargingGroupFormValues>;
};

export const ChargingGroupFormFields = ({ values, setFieldValue }: Props) => {
  const { t } = useTranslation();

  const [evseControllersPage, setEvseControllersPage] = useState(1);
  const [selectedType, setSelectedType] =
    useState<ChargingGroupType>('standard');
  const [searchFilterId, setSearchFilterId] = useState<number>(1);
  const [searchFilters, setSearchFilters] = useState<SearchFilter[]>([
    { id: searchFilterId, category: FilterType.Account, value: '' },
  ]);

  // This is used to search for charging stations
  // and it needs to be debounced to avoid too many requests
  // so I'm using a state to store the value and then debounce the request
  // instead of using the formik value
  const [searchPhraseChargingStations, setSearchPhraseChargingStations] =
    useState('');
  const [collapsedStations, setCollapsedStations] = useState<Set<string>>(
    new Set()
  );

  const [allCollapsed, setAllCollapsed] = useState(true);

  const selectedSearchFiltersAccountIds = searchFilters
    .filter((filter) => filter.category === FilterType.Account && filter.value)
    .map((filter) => filter.value);
  const selectedSearchFiltersLocationIds = searchFilters
    .filter((filter) => filter.category === FilterType.Location && filter.value)
    .map((filter) => filter.value);
  const selectedSearchFiltersPowerTypes = searchFilters
    .filter(
      (filter) => filter.category === FilterType.PowerType && filter.value
    )
    .map((filter) => filter.value);

  const evseControllersSearchQuery = {
    onlyWithConnectors: true,
    ...(isEmpty(searchPhraseChargingStations)
      ? undefined
      : { searchPhrase: searchPhraseChargingStations }),
    ...(isEmpty(selectedSearchFiltersAccountIds)
      ? undefined
      : { accountIds: selectedSearchFiltersAccountIds }),
    ...(isEmpty(selectedSearchFiltersLocationIds)
      ? undefined
      : { locationIds: selectedSearchFiltersLocationIds }),
    ...(isEmpty(selectedSearchFiltersPowerTypes)
      ? undefined
      : { powerType: first(selectedSearchFiltersPowerTypes) }),
    skip: (evseControllersPage - 1) * EVSE_CONTROLLER_LIMIT,
    limit: EVSE_CONTROLLER_LIMIT,
    sort: {
      field: 'createdAt',
      order: 'desc',
    },
  };

  const {
    data: response,
    isPending: loadingFetchingEvseControllers,
    error: errorFetchingEvseControllers,
  } = useQuery<{
    data: EvseController[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [
      EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL,
      evseControllersSearchQuery,
    ],
    queryFn: async () => {
      return await request({
        path: EVSE_CONTROLLERS_V1_FAST_SEARCH_API_URL,
        method: 'POST',
        body: evseControllersSearchQuery,
      });
    },
  });

  const evseControllers = response?.data ?? [];
  const evseControllersMeta = response?.meta ?? { total: 0, limit: 0 };
  const { connectorsSelection } = values;

  const connectors: ConnectorSelectionValueWithMetadata[] = useMemo(() => {
    const powerTypeFilter = searchFilters.find(
      (filter) => filter.category === FilterType.PowerType
    );

    return evseControllersToConnectorSelectionValues(evseControllers, {
      powerType: powerTypeFilter?.value,
    });
  }, [evseControllers, searchFilters]);

  const connectorsGroupedByOpChargingStationId = useMemo(
    () => groupBy(connectors, 'opChargingStationId'),
    [connectors]
  );

  const addConnectorToSelection = useCallback(
    (connector: ConnectorSelectionValue) => {
      const connectorSelectionKey = getConnectorSelectionKey(connector);

      setFieldValue('connectorsSelection', {
        ...connectorsSelection,
        [connectorSelectionKey]: {
          ...connector,
        },
      });
    },
    [connectorsSelection, setFieldValue]
  );

  const addConnectorsToSelection = useCallback(
    (connectors: ConnectorSelectionValue[]) => {
      const newConnectorsSelection = { ...connectorsSelection };
      connectors.forEach((connector) => {
        newConnectorsSelection[getConnectorSelectionKey(connector)] = connector;
      });
      setFieldValue('connectorsSelection', newConnectorsSelection);
    },
    [connectorsSelection, setFieldValue]
  );

  const removeConnectorFromSelection = useCallback(
    (connector: ConnectorSelectionValue) => {
      const connectorSelectionKey = getConnectorSelectionKey(connector);

      const newConnectorsSelection = { ...connectorsSelection };
      delete newConnectorsSelection[connectorSelectionKey];

      setFieldValue('connectorsSelection', newConnectorsSelection);
    },
    [connectorsSelection, setFieldValue]
  );

  const removeConnectorsFromSelection = useCallback(
    (connectors: ConnectorSelectionValue[]) => {
      const newConnectorsSelection = { ...connectorsSelection };
      connectors.forEach((connector) => {
        delete newConnectorsSelection[getConnectorSelectionKey(connector)];
      });
      setFieldValue('connectorsSelection', newConnectorsSelection);
    },
    [connectorsSelection, setFieldValue]
  );

  const handleSelectAll = useCallback(() => {
    const newConnectorsSelection = { ...connectorsSelection };

    connectors.forEach((connector) => {
      newConnectorsSelection[getConnectorSelectionKey(connector)] = connector;
    });

    setFieldValue('connectorsSelection', newConnectorsSelection);
  }, [connectors, connectorsSelection, setFieldValue]);

  const handleClearAll = useCallback(() => {
    const newConnectorsSelection = { ...connectorsSelection };

    connectors.forEach((connector) => {
      delete newConnectorsSelection[getConnectorSelectionKey(connector)];
    });

    setFieldValue('connectorsSelection', newConnectorsSelection);
  }, [connectorsSelection, setFieldValue]);

  const toggleStationCollapse = useCallback((opChargingStationId: string) => {
    setCollapsedStations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(opChargingStationId)) {
        newSet.delete(opChargingStationId);
      } else {
        newSet.add(opChargingStationId);
      }
      return newSet;
    });
  }, []);

  const isStationCollapsed = useCallback(
    (opChargingStationId: string) => {
      return !collapsedStations.has(opChargingStationId);
    },
    [collapsedStations]
  );

  const toggleAllStationsCollapse = useCallback(() => {
    const allStationIds = Object.keys(connectorsGroupedByOpChargingStationId);
    if (allCollapsed) {
      setCollapsedStations(new Set(allStationIds));
    } else {
      setCollapsedStations(new Set());
    }
    setAllCollapsed(!allCollapsed);
  }, [allCollapsed, connectorsGroupedByOpChargingStationId]);

  const handleSearchChargingStations = (value: string) => {
    setSearchPhraseChargingStations(value);
    setEvseControllersPage(1);
    setAllCollapsed(true);
    setCollapsedStations(new Set());
  };

  const debouncedHandleSearchChargingStations = useMemo(
    () => debounce(handleSearchChargingStations, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearchChargingStations.cancel(); // prevents memory leaks and race conditions
    };
  }, [debouncedHandleSearchChargingStations]);

  const changeSearchFilter = useCallback(
    (id: number, category: FilterType, value: string) => {
      setEvseControllersPage(1);
      setSearchFilters(
        searchFilters.map((filter) =>
          filter.id === id ? { ...filter, category, value } : filter
        )
      );
      setAllCollapsed(true);
      setCollapsedStations(new Set());
    },
    [setEvseControllersPage, searchFilters, setSearchFilters]
  );

  const addNewSearchFilter = useCallback(() => {
    const newSearchFilterId = searchFilterId + 1;
    setSearchFilterId(newSearchFilterId);
    setSearchFilters([
      ...searchFilters,
      { id: newSearchFilterId, category: FilterType.Account, value: '' },
    ]);
  }, [searchFilterId, searchFilters, setSearchFilterId, setSearchFilters]);

  const removeSearchFilter = useCallback(
    (id: number) => {
      setSearchFilters(searchFilters.filter((filter) => filter.id !== id));
      setEvseControllersPage(1);
      setAllCollapsed(true);
      setCollapsedStations(new Set());
    },
    [searchFilters, setSearchFilters, setEvseControllersPage]
  );

  const handlePageChange = useCallback((page: number) => {
    setEvseControllersPage(page);
    setAllCollapsed(true);
    setCollapsedStations(new Set());
  }, []);

  const error = errorFetchingEvseControllers;
  const totalSelected = size(connectorsSelection);
  const allConnectorsSelectedInPage = connectors.every(
    (connector) => connectorsSelection[getConnectorSelectionKey(connector)]
  );
  const someConnectorsSelectedInPage = connectors.some(
    (connector) => connectorsSelection[getConnectorSelectionKey(connector)]
  );

  return (
    <>
      <Header
        as="h4"
        content={t(
          'providerAdmin.chargingGroupForm.addGroupTitle',
          '1. Add group name and description'
        )}
      />
      <p>
        {t(
          'providerAdmin.chargingGroupForm.addGroupDescription',
          'Add a unique name and a description to easily identify this group later.'
        )}
      </p>
      <InputField
        name="label"
        required
        label={t('providerAdmin.chargingGroupForm.nameLabel', 'Name')}
      />
      <InputField
        name="description"
        label={t(
          'providerAdmin.chargingGroupForm.descriptionLabel',
          'Description'
        )}
      />

      <Header
        as="h4"
        content={t(
          'providerAdmin.chargingGroupForm.selectStationsTitle',
          '2. Select type of charging group'
        )}
      />
      <p>
        {t(
          'providerAdmin.chargingGroupForm.selectStationsDescription',
          'Define if you want to manually add connectors or they are added automatically'
        )}
      </p>
      <div>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingGroupForm.typeOfGroupTitle',
            'Type of group'
          )}
          style={{ paddingTop: 10 }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <SelectableButton
            selected={selectedType === 'standard'}
            onClick={() => setSelectedType('standard')}>
            <div>
              <p style={{ marginBottom: 6 }}>
                <strong>
                  {t(
                    'providerAdmin.chargingGroupForm.typeStandardTitle',
                    'Standard'
                  )}
                </strong>
              </p>
              <p style={{ marginBottom: 6, fontSize: 12 }}>
                {t(
                  'providerAdmin.chargingGroupForm.typeStandardDescription',
                  'Select connectors manually'
                )}
              </p>
            </div>
          </SelectableButton>
          <SelectableButton
            selected={selectedType === 'smart'}
            onClick={() => setSelectedType('smart')}
            disabled>
            <div>
              <p style={{ marginBottom: 6 }}>
                <strong>
                  {t('providerAdmin.chargingGroupForm.typeSmartTitle', 'Smart')}
                </strong>
              </p>
              <p style={{ marginBottom: 6, fontSize: 12 }}>
                {t(
                  'providerAdmin.chargingGroupForm.typeSmartDescription',
                  'Set a rule that automatically includes connectors'
                )}
              </p>
            </div>
          </SelectableButton>
        </div>
      </div>
      <div>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingGroupForm.searchForConnectorsTitle',
            '3. Search for connectors'
          )}
          style={{ paddingTop: 10 }}
        />
        <p>
          {t(
            'providerAdmin.chargingGroupForm.searchForConnectorsDescription',
            'Filter the table below to find specific connectors to select'
          )}
        </p>
        <div>
          {size(searchFilters) > 0 &&
            searchFilters.map((filter, index) => {
              const categoryOptions = searchFilters
                .slice(0, index)
                .some((f) => f.category === FilterType.PowerType)
                ? SEARCH_CATEGORY_FILTERS.filter(
                    (f) => f.value !== FilterType.PowerType
                  )
                : SEARCH_CATEGORY_FILTERS;

              return (
                <div
                  key={filter.id}
                  style={{
                    display: 'flex',
                    gap: 10,
                    width: '100%',
                    marginBottom: 10,
                  }}>
                  <div style={{ width: 400 }}>
                    <Form.Field disabled={false} required={false}>
                      <label>
                        {t(
                          'providerAdmin.chargingGroupForm.categoryLabel',
                          'Category'
                        )}
                      </label>
                      <Dropdown
                        selection
                        options={categoryOptions}
                        value={filter.category}
                        clearable={false}
                        onChange={(_, { value }) =>
                          changeSearchFilter(filter.id, value as FilterType, '')
                        }
                      />
                    </Form.Field>
                  </div>
                  <div style={{ width: '100%' }}>
                    {filter.category === FilterType.PowerType && (
                      <Form.Field disabled={false} required={false}>
                        <label>
                          {t(
                            'providerAdmin.chargingGroupForm.valueLabel',
                            'Value'
                          )}
                        </label>
                        <Dropdown
                          options={SEARCH_POWER_TYPE_FILTERS}
                          value={filter.value}
                          fluid
                          selection
                          clearable={false}
                          onChange={(_, { value }) => {
                            changeSearchFilter(
                              filter.id,
                              filter.category,
                              value as string
                            );
                          }}
                        />
                      </Form.Field>
                    )}
                    {filter.category === FilterType.Account && (
                      <SearchDropdown
                        label={t(
                          'providerAdmin.chargingGroupForm.valueLabel',
                          'Value'
                        )}
                        onDataNeeded={async ({ name }: { name: string }) => {
                          return await request({
                            path: ACCOUNTS_V1_FAST_SEARCH_API_URL,
                            method: 'POST',
                            body: { searchPhrase: name },
                          });
                        }}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                          { value }: { value?: Account }
                        ) => {
                          changeSearchFilter(
                            filter.id,
                            filter.category,
                            value?.id ?? ''
                          );
                        }}
                        placeholder={t(
                          'providerAdmin.chargingGroupForm.selectOptionPlaceholderText',
                          'Select Option'
                        )}
                      />
                    )}
                    {filter.category === FilterType.Location && (
                      <SearchDropdown
                        label={t(
                          'providerAdmin.chargingGroupForm.valueLabel',
                          'Value'
                        )}
                        onDataNeeded={async ({ name }: { name: string }) => {
                          return await request({
                            path: LOCATIONS_V2_CPO_FAST_SEARCH_API_URL,
                            method: 'POST',
                            body: { searchPhrase: name },
                          });
                        }}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                          { value }: { value?: Location }
                        ) => {
                          changeSearchFilter(
                            filter.id,
                            filter.category,
                            value?.id ?? ''
                          );
                        }}
                        placeholder={t(
                          'providerAdmin.chargingGroupForm.selectOptionPlaceholderText',
                          'Select Option'
                        )}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'end',
                      marginBottom: 1,
                    }}>
                    <Button
                      as="button"
                      icon="trash"
                      basic
                      disabled={size(searchFilters) === 1}
                      onClick={() => removeSearchFilter(filter.id)}
                    />
                  </div>
                </div>
              );
            })}
          <ActionButtonWithIcon
            text={t(
              'providerAdmin.chargingGroupForm.addFilterButtonText',
              'Add another filter'
            )}
            icon="plus"
            onClick={() => addNewSearchFilter()}
          />
        </div>
      </div>
      <div>
        <Header
          as="h4"
          content={t(
            'providerAdmin.chargingGroupForm.selectConnectorsTitle',
            '4. Select connectors'
          )}
          style={{ paddingTop: 10 }}
        />
        <p>
          {t(
            'providerAdmin.chargingGroupForm.selectConnectorsDescription',
            'Choose the connectors you want to add to your charging group'
          )}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 30,
            marginBottom: 10,
            alignItems: 'center',
          }}>
          <strong>
            {t(
              'providerAdmin.chargingGroupForm.foundItemsText',
              '{{count}} charging stations found',
              {
                count: evseControllersMeta?.total ?? 0,
              }
            )}
          </strong>
          <div style={{ width: 500 }}>
            <InputField
              name="searchChargingStations"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                debouncedHandleSearchChargingStations(e.target.value);
              }}
              placeholder={t(
                'providerAdmin.chargingGroupForm.searchChargingStationsPlaceholderText',
                'Search by EVSE ID*, OCPP ID or Account name'
              )}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                // Prevents form submission when Enter is pressed
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </div>
        </div>

        <div style={{ paddingBottom: 10 }}>
          {loadingFetchingEvseControllers && <Loader active />}
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {size(connectors) > 0 && (
                    <Icon
                      name={allCollapsed ? 'angle-right' : 'angle-down'}
                      style={{ cursor: 'pointer' }}
                      onClick={toggleAllStationsCollapse}
                    />
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {size(connectors) > 0 && (
                    <Checkbox
                      checked={allConnectorsSelectedInPage}
                      indeterminate={
                        someConnectorsSelectedInPage &&
                        !allConnectorsSelectedInPage
                      }
                      onChange={(
                        e: React.FormEvent<HTMLInputElement>,
                        { checked }: { checked?: boolean }
                      ) => {
                        if (checked) {
                          handleSelectAll();
                        } else {
                          handleClearAll();
                        }
                      }}
                    />
                  )}
                </Table.HeaderCell>
                <Table.HeaderCell>EVSE ID</Table.HeaderCell>
                <Table.HeaderCell>OCPP ID</Table.HeaderCell>
                <Table.HeaderCell>Account</Table.HeaderCell>
                <Table.HeaderCell>Info</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {size(connectors) === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6} style={{ textAlign: 'center' }}>
                    {loadingFetchingEvseControllers
                      ? t(
                          'providerAdmin.chargingGroupForm.loadingChargingStations',
                          'Loading charging stations...'
                        )
                      : t(
                          'providerAdmin.chargingGroupForm.noChargingStationsFound',
                          'No charging stations found'
                        )}
                  </Table.Cell>
                </Table.Row>
              )}
              {Object.entries(connectorsGroupedByOpChargingStationId).map(
                ([opChargingStationId, stationConnectors]) => {
                  const allConnectorsInStationSelected =
                    stationConnectors.every(
                      (connector) =>
                        connectorsSelection[getConnectorSelectionKey(connector)]
                    );
                  const someConnectorsInStationSelected =
                    stationConnectors.some(
                      (connector) =>
                        connectorsSelection[getConnectorSelectionKey(connector)]
                    );

                  return (
                    <React.Fragment key={opChargingStationId}>
                      <Table.Row
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                        <Table.Cell style={{ paddingRight: 0 }}>
                          <Icon
                            name={
                              isStationCollapsed(opChargingStationId)
                                ? 'angle-right'
                                : 'angle-down'
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              toggleStationCollapse(opChargingStationId)
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={allConnectorsInStationSelected}
                            indeterminate={
                              someConnectorsInStationSelected &&
                              !allConnectorsInStationSelected
                            }
                            onChange={(
                              e: React.FormEvent<HTMLInputElement>,
                              { checked }: { checked?: boolean }
                            ) => {
                              if (checked) {
                                addConnectorsToSelection(stationConnectors);
                              } else {
                                removeConnectorsFromSelection(
                                  stationConnectors
                                );
                              }
                            }}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.evseId || '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.ocppIdentity ||
                            '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {stationConnectors[0]?.evseController.account?.name ||
                            '-'}
                        </Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                      {/* Connectors for this station */}
                      {!isStationCollapsed(opChargingStationId) &&
                        stationConnectors.map((connector) => {
                          const selected =
                            !!connectorsSelection[
                              getConnectorSelectionKey(connector)
                            ];

                          return (
                            <Table.Row
                              key={getConnectorSelectionKey(connector)}
                              style={{ paddingLeft: 20 }}>
                              <Table.Cell />
                              <Table.Cell>
                                <Checkbox
                                  checked={selected}
                                  onChange={(
                                    e: React.FormEvent<HTMLInputElement>,
                                    { checked }: { checked?: boolean }
                                  ) => {
                                    if (checked) {
                                      addConnectorToSelection(connector);
                                    } else {
                                      removeConnectorFromSelection(connector);
                                    }
                                  }}
                                />
                              </Table.Cell>
                              <Table.Cell>{connector.evseId || '-'}</Table.Cell>
                              <Table.Cell />
                              <Table.Cell />
                              <Table.Cell>
                                {connector.powerType && connector.standard ? (
                                  <>
                                    {powerTypeToFriendlyName(
                                      connector.powerType
                                    )}{' '}
                                    (
                                    {standardToFriendlyName(connector.standard)}
                                    )
                                  </>
                                ) : (
                                  '-'
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                    </React.Fragment>
                  );
                }
              )}
            </Table.Body>
          </Table>
          <ErrorMessage error={errorFetchingEvseControllers} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              className=""
              estimated=""
              limit={EVSE_CONTROLLER_LIMIT}
              page={evseControllersPage}
              total={evseControllersMeta?.total ?? 0}
              onPageChange={(
                e: React.ChangeEvent<HTMLInputElement>,
                { activePage }: { activePage: number }
              ) => {
                handlePageChange(activePage);
              }}
            />
          </div>
        </div>
      </div>
      <Header
        as="h4"
        content={t(
          'providerAdmin.chargingGroupForm.reviewTitle',
          '5. Review charging group'
        )}
      />
      <p>
        {t(
          'providerAdmin.chargingGroupForm.reviewDescription',
          'Preview how the group will be created'
        )}
      </p>
      <ChargingGroupPreview
        label={values.label}
        numConnectors={totalSelected}
        actionComponents={
          <ChargingGroupReviewModal
            connectorsSelection={connectorsSelection}
            trigger={
              <Button
                icon="eye"
                basic
                size="mini"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                }}></Button>
            }
            onSubmit={(connectorsSelection) => {
              setFieldValue('connectorsSelection', connectorsSelection);
            }}
          />
        }
      />
      <ErrorMessage error={error} />
    </>
  );
};
